import type { ChatMessage, ClientSession, ClientSnapshot } from '@campfire/net';
import { CHAT_HISTORY, CHAT_MAX_LENGTH } from '@campfire/net';
import { h } from './dom.ts';
import { shortAge } from './age.ts';

/**
 * Table chat (FR-CHAT-01/02). Lives outside the app root so table re-renders never steal the
 * input's focus. Two modes: an overlay of translucent bubbles over the table (Whatnot-style), or
 * slid away into a tray pill that counts unread lines.
 */
export interface ChatPanel {
  update(s: ClientSnapshot): void;
  remove(): void;
}

let mode: 'overlay' | 'tray' = 'overlay';
let showTimes = false;
/** Collapsed while playing: the last few lines show but taps fall through to the table. */
let expanded = false;


export function mountChat(client: ClientSession): ChatPanel {
  let lastId = 0;
  let unread = 0;
  const list = h('div.chat-list', { 'data-testid': 'chat-list', role: 'log', 'aria-live': 'polite' });
  const input = h('input.chat-input', { 'data-testid': 'chat-input', placeholder: 'Say something…', maxlength: CHAT_MAX_LENGTH, enterkeyhint: 'send', autocomplete: 'off' }) as HTMLInputElement;
  const send = () => {
    const text = input.value.trim();
    if (!text) return;
    client.say(text);
    input.value = '';
  };
  input.addEventListener('keydown', (e) => e.key === 'Enter' && send());
  const badge = h('span.chat-badge', { 'data-testid': 'chat-unread' });
  // Scrolled back through history? Offer a jump to the newest line instead of yanking the view.
  const latest = h('button.chat-latest', { 'data-testid': 'chat-latest', 'aria-label': 'Jump to latest', onclick: () => toBottom() }, '⤓');
  const atBottom = () => list.scrollHeight - list.scrollTop - list.clientHeight < 24;
  const toBottom = () => {
    list.scrollTop = list.scrollHeight;
    latest.hidden = true;
  };
  latest.hidden = true;
  list.addEventListener('scroll', () => (latest.hidden = atBottom()));
  const lines: { el: HTMLElement; at: number }[] = [];
  const paintTimes = () => {
    const t = Date.now();
    for (const l of lines) (l.el.firstElementChild as HTMLElement).textContent = showTimes ? shortAge(l.at, t) : '';
    el.classList.toggle('chat-times-on', showTimes);
  };
  const clock = setInterval(paintTimes, 30_000);
  const pill = h('button.chat-pill', { 'data-testid': 'chat-open', 'aria-label': 'Open chat', onclick: () => setMode('overlay') }, '💬 Chat ', badge);
  const panel = h(
    'div.chat-panel',
    {},
    h('div.chat-scroll', {}, list, latest),
    h(
      'div.chat-row',
      {},
      input,
      h('button.chat-send', { 'data-testid': 'chat-send', onclick: send, 'aria-label': 'Send' }, '➤'),
      h('button.chat-more', { 'data-testid': 'chat-more', 'aria-label': 'Show chat history', onclick: () => setExpanded(!expanded) }, '⌃'),
      h('button.chat-clock', { 'data-testid': 'chat-times', 'aria-label': 'Show times', onclick: () => ((showTimes = !showTimes), paintTimes()) }, '🕒'),
      h('button.chat-hide', { 'data-testid': 'chat-hide', onclick: () => setMode('tray'), 'aria-label': 'Slide chat away' }, '⌄'),
    ),
  );
  const el = h('div.chat', { 'data-testid': 'chat' }, panel, pill);
  document.body.append(el);
  // Reserve room under the page so the chat bar never covers the table's own buttons.
  document.body.classList.add('has-chat');

  input.addEventListener('focus', () => setExpanded(true));
  // Any tap outside the chat folds it back down so the table is usable again.
  document.addEventListener('pointerdown', (e) => expanded && !el.contains(e.target as Node) && setExpanded(false), true);
  function setExpanded(on: boolean): void {
    expanded = on;
    paint();
    if (!on) toBottom();
  }
  function setMode(m: typeof mode): void {
    mode = m;
    if (m === 'overlay') unread = 0;
    paint();
  }
  function paint(): void {
    el.className = `chat chat-${mode}${expanded ? ' chat-expanded' : ''}${showTimes ? ' chat-times-on' : ''}`;
    badge.textContent = unread ? String(unread) : '';
  }
  const line = (m: ChatMessage, me: string | null) => {
    const el = h(`div.chat-line${m.from === me ? '.mine' : ''}`, { 'data-testid': 'chat-line' }, h('span.chat-age', { 'data-testid': 'chat-age' }, showTimes ? shortAge(m.at, Date.now()) : ''), h('b', {}, m.name), ' ', m.text);
    lines.push({ el, at: m.at });
    if (lines.length > CHAT_HISTORY) lines.shift();
    return el;
  };

  paint();
  return {
    update(s) {
      // Lines are numbered by the host; anything newer than the last one shown is appended.
      const fresh = s.chat.filter((m) => m.id > lastId);
      if (!fresh.length) return;
      const follow = atBottom() || lastId === 0;
      for (const m of fresh) list.append(line(m, s.id));
      while (list.childElementCount > CHAT_HISTORY) list.firstElementChild?.remove();
      if (mode === 'tray') unread += fresh.filter((m) => m.from !== s.id).length;
      lastId = fresh[fresh.length - 1]!.id;
      if (follow) toBottom();
      else latest.hidden = false;
      paint();
    },
    remove: () => {
      clearInterval(clock);
      el.remove();
      document.body.classList.remove('has-chat');
    },
  };
}
